/**
 * Pure role-check helper functions extracted from AuthContext.jsx.
 *
 * Every function accepts (userProfile, godModeEmails) so the logic is
 * framework-agnostic and trivially testable. The godModeEmails parameter
 * defaults to the canonical GOD_MODE_EMAILS list when omitted.
 */

// GOD MODE: These emails ALWAYS have admin role
export const GOD_MODE_EMAILS = ['aj@snshn.co'];

/**
 * Returns true when the given email is in the god-mode list.
 * Comparison is case-insensitive.
 */
function isGodModeEmail(email, godModeEmails = GOD_MODE_EMAILS) {
  if (!email) return false;
  return godModeEmails.includes(email.toLowerCase());
}

/**
 * Check if a user is admin (includes GOD_MODE).
 */
export function isAdmin(userProfile, godModeEmails = GOD_MODE_EMAILS) {
  if (!userProfile) return false;
  if (isGodModeEmail(userProfile.email, godModeEmails)) return true;
  return userProfile.role === 'admin';
}

/**
 * Check if a user is PM or higher (includes GOD_MODE).
 */
export function isPM(userProfile, godModeEmails = GOD_MODE_EMAILS) {
  if (!userProfile) return false;
  if (isGodModeEmail(userProfile.email, godModeEmails)) return true;
  return ['pm', 'admin'].includes(userProfile.role);
}

/**
 * Check if a user is demo (read-only access).
 * GOD_MODE users are NEVER demo, even if their profile says so.
 */
export function isDemo(userProfile, godModeEmails = GOD_MODE_EMAILS) {
  if (!userProfile) return false;
  if (isGodModeEmail(userProfile.email, godModeEmails)) return false;
  return userProfile.role === 'demo';
}

/**
 * Check if a user can write/edit (not demo).
 * GOD_MODE users can ALWAYS write.
 */
export function canWrite(userProfile, godModeEmails = GOD_MODE_EMAILS) {
  if (!userProfile) return false;
  if (isGodModeEmail(userProfile.email, godModeEmails)) return true;
  return userProfile.role !== 'demo';
}

/**
 * Determine the role to assign to a brand-new user.
 * GOD_MODE emails get 'admin'; everyone else gets 'tech'.
 */
export function determineNewUserRole(email, godModeEmails = GOD_MODE_EMAILS) {
  return isGodModeEmail(email, godModeEmails) ? 'admin' : 'tech';
}
